import type { ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import type { FeatureFlagKeyType } from '@scrambl/shared'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { useOrganization } from '@/hooks/useOrganization'
import { Loader2 } from 'lucide-react'

interface FeatureFlagGuardProps {
  flagKey: FeatureFlagKeyType
  children: ReactNode
}

/**
 * Route guard that checks if a feature flag is enabled for the current organization.
 * Redirects to the org's today page if the feature is disabled.
 */
export function FeatureFlagGuard({ flagKey, children }: FeatureFlagGuardProps) {
  const { isLoading, checkFeatureFlag } = useFeatureFlags([flagKey])
  const { currentOrganization } = useOrganization()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[200px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!checkFeatureFlag(flagKey)) {
    const orgSlug = currentOrganization?.slug ?? ''
    return <Navigate to={`/${orgSlug}/today`} replace />
  }

  return <>{children}</>
}
